import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    usePlandokumenterForPlan,
    useCreatePlandokument,
    useFetchPlandokumentAttachmentUrl,
    plandokumentInputSchema,
} from '@origo/planregister';
import { Loader, Notice } from '@origo/origo-ui';
import { useToast } from '@origo/origo-components';
import DocumentsPage, { type NewDocumentState } from './DocumentsPage';
import type { DocumentFieldErrors } from './components/DocumentForm';
import { emptyDocumentForm, toDocumentItem, toPlandokumentInput } from '../../mappers/toDocuments';
import type { DocumentFormValues } from '../../types';

interface DocumentsContainerProps {
    napId: string;
}

/** Feltnære feil fra pakkens zod-schema (jf. ProcessingsContainer). */
function collectFieldErrors(input: unknown): DocumentFieldErrors | null {
    const parsed = plandokumentInputSchema.safeParse(input);
    if (parsed.success) return null;
    const next: DocumentFieldErrors = {};
    for (const issue of parsed.error.issues) {
        const key = issue.path[0] as keyof DocumentFormValues | undefined;
        if (key && !next[key]) next[key] = issue.message;
    }
    return next;
}

const DocumentsContainer: React.FC<DocumentsContainerProps> = ({ napId }) => {
    const { t } = useTranslation();
    const toast = useToast();
    const { data, isLoading, isError } = usePlandokumenterForPlan(napId);
    const create = useCreatePlandokument();
    const fetchAttachmentUrl = useFetchPlandokumentAttachmentUrl();

    // Nyeste øverst — sorterer på dokumentdato (desc). Dokumenter uten
    // dato havner sist, sortert stabilt etter opprinnelig array-orden.
    const items = useMemo(() => {
        const sorted = [...(data ?? [])].sort((a, b) => {
            const aTime = a.dokumentdato?.getTime() ?? -Infinity;
            const bTime = b.dokumentdato?.getTime() ?? -Infinity;
            return bTime - aTime;
        });
        return sorted.map(doc => toDocumentItem(doc, t));
    }, [data, t]);

    // --- nytt dokument (F3.1) ---
    const [newOpen, setNewOpen] = useState(false);
    const [newDraft, setNewDraft] = useState<DocumentFormValues>(() => emptyDocumentForm());
    const [newFile, setNewFile] = useState<File | null>(null);
    const [newErrors, setNewErrors] = useState<DocumentFieldErrors>({});
    const [fileError, setFileError] = useState<string | undefined>(undefined);

    const openNew = useCallback(() => {
        setNewDraft(emptyDocumentForm());
        setNewFile(null);
        setNewErrors({});
        setFileError(undefined);
        setNewOpen(true);
    }, []);

    const changeNew = useCallback((field: keyof DocumentFormValues, value: string) => {
        setNewDraft(d => ({ ...d, [field]: value }));
        setNewErrors(e => (e[field] ? { ...e, [field]: undefined } : e));
    }, []);

    const changeNewFile = useCallback((file: File | null) => {
        setNewFile(file);
        setFileError(undefined);
    }, []);

    const saveNew = useCallback(async () => {
        const input = toPlandokumentInput(newDraft, napId);
        const errors = collectFieldErrors(input);
        const missingFile = !newFile;
        setNewErrors(errors ?? {});
        setFileError(missingFile ? t('documents.new.fileRequired') : undefined);
        if (errors || missingFile) return;

        const created = await toast.execute(() => create.mutateAsync({ input, file: newFile ?? undefined }), {
            success: t('documents.uploaded'),
        });
        if (created) {
            setNewOpen(false);
            setNewDraft(emptyDocumentForm());
            setNewFile(null);
        }
    }, [newDraft, newFile, napId, toast, create, t]);

    if (isLoading) return <Loader label={t('common.loading')} />;
    if (isError) return <Notice variant="danger">{t('common.errorLoading')}</Notice>;

    const newDocument: NewDocumentState = {
        open: newOpen,
        values: newDraft,
        file: newFile,
        fieldErrors: newErrors,
        fileError,
        isSaving: create.isPending,
    };

    return (
        <DocumentsPage
            items={items}
            onOpen={async id => {
                const doc = data?.find(d => d.dokumentId === id);
                if (!doc?.referansedokumentfil) return;
                const blobUrl = await fetchAttachmentUrl(doc.referansedokumentfil);
                if (blobUrl) window.open(blobUrl, '_blank', 'noopener,noreferrer');
            }}
            newDocument={newDocument}
            onNewOpen={openNew}
            onNewClose={() => setNewOpen(false)}
            onNewChange={changeNew}
            onNewFileChange={changeNewFile}
            onNewSave={saveNew}
        />
    );
};

export default DocumentsContainer;
